import { soundNotice, getVolume, beatPulse } from "./a11yBus";
import * as Tone from "tone";
import { STYLES } from "@/data/playalongStyles";
import { barKind, hitBar } from "@/data/playalong";

const PC = { C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3, E: 4, F: 5, "F#": 6, Gb: 6, G: 7, "G#": 8, Ab: 8, A: 9, "A#": 10, Bb: 10, B: 11 };
const SHARP = ["C", "C#", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"];
const NUM = { I: 0, II: 2, III: 4, IV: 5, V: 7, VI: 9, VII: 11 };
const QUAL = { "": [0, 4, 7], m: [0, 3, 7], "7": [0, 4, 7, 10], m7: [0, 3, 7, 10], maj7: [0, 4, 7, 11], m7b5: [0, 3, 6, 10], sus4: [0, 5, 7], "6": [0, 4, 7, 9], m6: [0, 3, 7, 9] };
const QLABEL = { "": "", m: "m", "7": "7", m7: "m7", maj7: "maj7", m7b5: "m7♭5", sus4: "sus4", "6": "6", m6: "m6" };

export const keyName = (pc) => SHARP[((pc % 12) + 12) % 12];
export const keyPc = (name) => PC[name];

// "bVII7", "ii7", "V7@2" (numeral relative to key + 2 semitones)
export function parseChord(sym, keyRoot) {
  const [body, shift] = sym.split("@");
  const m = body.match(/^([b#]?)(VII|VI|IV|V|III|II|I|vii|vi|iv|v|iii|ii|i)(.*)$/);
  if (!m) return null;
  const minor = m[2] === m[2].toLowerCase();
  let q = m[3] || "";
  if (minor && (q === "" || q === "7" || q === "6")) q = q === "" ? "m" : `m${q}`;
  const acc = m[1] === "b" ? -1 : m[1] === "#" ? 1 : 0;
  const root = (keyRoot + NUM[m[2].toUpperCase()] + acc + Number(shift || 0) + 120) % 12;
  return { root, intervals: QUAL[q] || QUAL[""], label: `${keyName(root)}${QLABEL[q] ?? ""}` };
}

export const barChords = (bar, keyRoot) => bar.split(" ").map((s) => parseChord(s, keyRoot)).filter(Boolean);
// Where each chord in a bar starts: one chord = whole bar, two = half each, three = beat 1, beat 2, then the second half.
export const chordStarts = (n, steps) => (n >= 3 ? [0, Math.floor(steps / 4), Math.floor(steps / 2)] : n === 2 ? [0, Math.floor(steps / 2)] : [0]);

const fit = (midi, lo, hi) => { let n = midi; while (n < lo) n += 12; while (n > hi) n -= 12; return n; };
const midiOf = (note) => { const m = note.match(/^([A-G][b#]?)(-?\d)$/); return (Number(m[2]) + 1) * 12 + PC[m[1]]; };

const S = "/media/samples";
const urls = (folder, names) => ({ baseUrl: `${S}/${folder}/`, urls: Object.fromEntries(names.map((n) => [Tone.Frequency(midiOf(n), "midi").toNote(), `${n}.mp3`])) });

// Virtuosity Drums (CC0): velocity layers x round robins.
const KIT = `${S}/kit`;
const FAM = { k: "kick", s: "snare", h: "hhc", o: "hho", p: "hhp", t1: "tomhi", t2: "tomlo", t3: "tomlo", r: "ride", b: "ridebell", c: "crash", x: "sidestick" };
const RATE = { t2: 1.18 };
async function loadKit() {
  const man = await fetch(`${KIT}/manifest.json`).then((res) => res.json());
  const bufs = {};
  await Promise.all(Object.entries(man).map(async ([fam, layers]) => {
    bufs[fam] = await Promise.all(layers.map((rrs) => Promise.all(rrs.map((f) => Tone.ToneAudioBuffer.fromUrl(`${KIT}/${f}`)))));
  }));
  return bufs;
}
const human = (amt) => (Math.random() - 0.5) * amt;
// Salamander Grand Piano (CC-BY 3.0, Alexander Holm) — sampled every minor third.
const PIANO = ["C2", "Ds2", "Fs2", "A2", "C3", "Ds3", "Fs3", "A3", "C4", "Ds4", "Fs4", "A4", "C5", "Ds5", "Fs5", "A5", "C6"];
const pianoUrls = { baseUrl: `${S}/piano2/`, urls: Object.fromEntries(PIANO.map((n) => [n.replace("s", "#"), `${n}.mp3`])) };

let rig = null;
async function getRig() {
  if (rig) return rig;
  const out = new Tone.Gain(0.9).toDestination();
  const clickOut = new Tone.Gain(0.8).toDestination();
  const glue = new Tone.Compressor({ threshold: -18, ratio: 3, attack: 0.01, release: 0.2 }).connect(out);
  const reverb = new Tone.Reverb({ decay: 2.2, wet: 0.2 }).connect(glue);
  const drumBus = new Tone.Volume(-3).connect(glue);
  const drumRoom = new Tone.Volume(-14).connect(reverb);
  const bus = (vol) => new Tone.Volume(vol).connect(reverb);
  const sampler = (folder, names, vol, extra = {}) => new Tone.Sampler({ ...urls(folder, names), release: 0.6, ...extra }).connect(bus(vol));
  rig = {
    piano: new Tone.Sampler({ ...pianoUrls, release: 1 }).connect(bus(-9)),
    bass: sampler("bass", ["A0", "C1", "Eb1", "Gb1", "A1", "C2", "Eb2", "Gb2", "A2", "C3"], -4, { release: 0.2 }),
    guitar: sampler("guitar", ["C3", "Eb3", "Gb3", "A3", "C4", "Eb4", "Gb4", "A4", "C5"], -9),
    sax: sampler("sax", ["Db3", "E3", "G3", "Bb3", "Db4", "E4", "G4", "Bb4", "Db5", "E5"], -9),
    voice: sampler("voice", ["C3", "Eb3", "Gb3", "A3", "C4", "Eb4", "Gb4", "A4", "C5", "Eb5"], -10, { attack: 0.08, release: 1.2 }),
    kit: await loadKit(), drumBus, drumRoom, openHat: null,
    click: new Tone.Synth({ oscillator: { type: "triangle" }, envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.02 } }).connect(clickOut),
    out, clickOut,
  };
  await Tone.loaded();
  return rig;
}

function hit(r, voice, vel, time) {
  const fam = FAM[voice] || voice, layers = r.kit[fam];
  if (!layers) return;
  const v = Math.min(1, Math.max(0.05, vel * (0.92 + Math.random() * 0.14)));
  const rrs = layers[Math.min(layers.length - 1, Math.floor(v * layers.length))];
  const t = voice === "k" ? time : time + Math.max(0, human(0.012));
  if (fam === "hhc" || fam === "hhp") { r.openHat?.stop(t); r.openHat = null; }
  const src = new Tone.ToneBufferSource({ url: rrs[Math.floor(Math.random() * rrs.length)], playbackRate: RATE[voice] || 1 });
  src.connect(r.drumBus);
  src.connect(r.drumRoom);
  src.start(t, 0, undefined, 0.35 + v * 0.65);
  if (fam === "hho") r.openHat = src;
}

const STEP_DUR = { 2: "8n", 3: "8t", 4: "16n" };

// track: { meter, beats, sub, steps, style, key, bpm, bars:[chords], melody?:[[["G4",1],...]...] }
// opts: { parts:{drums,bass,keys,guitar,lead}, lead:"voice"|"sax", semitones, bpm, onBar(barIdx), guideMelody }
export async function startTrack(track, opts) {
  await Tone.start();
  const r = await getRig();
  stopTrack();
  if (opts.bandVolume != null) r.out.gain.value = opts.bandVolume;
  if (opts.clickVolume != null) r.clickOut.gain.value = opts.clickVolume;
  Tone.getDestination().volume.value = Tone.gainToDb(Math.max(0.001, getVolume()));
  soundNotice(`Backing track playing${track.title ? `: ${track.title}` : ""}`);
  const style = STYLES[track.style];
  const { beats, sub, steps } = style;
  const T = Tone.getTransport();
  T.bpm.value = opts.bpm;
  T.swing = style.swing || 0;
  T.swingSubdivision = "8n";
  const keyRoot = (keyPc(track.key) + opts.semitones + 12) % 12;
  const stepSec = () => Tone.Time(STEP_DUR[sub]).toSeconds();
  const countSteps = Math.round(beats * sub);
  const melodyEvents = (track.melody && track.melody.length === track.bars.length ? track.melody : []).map((bar) => {
    let pos = 0;
    return bar.map(([n, d]) => { const ev = { step: pos, note: n, len: Math.round(d * sub) }; pos += ev.len; return ev; });
  });
  let tick = 0;
  const startOffset = opts.start ? opts.start.bar * steps + opts.start.step : 0;

  const step = (time) => {
    if (tick < countSteps) {
      if (tick % sub === 0) r.click.triggerAttackRelease(tick === 0 ? "C6" : "G5", "32n", time, 0.6);
      tick += 1;
      return;
    }
    const t = tick - countSteps + startOffset;
    const L = opts.loop;
    const span = L ? L.end - L.start + 1 : track.bars.length;
    const rel = Math.floor(t / steps) - (L ? L.start : 0);
    const barIdx = (L ? L.start : 0) + (((rel % span) + span) % span);
    const st = t % steps;
    const kind = barKind(track, barIdx, style);
    const stab = [[0, sub]];
    const chords = barChords(track.bars[barIdx], keyRoot);
    const starts = chordStarts(chords.length, steps);
    const ci = starts.reduce((a, s, i) => (st >= s ? i : a), 0);
    const chord = chords[ci];
    // A chord that lands where the groove has no hit still gets its root and a stab.
    const freshHit = ci > 0 && st === starts[ci] ? [[st, starts[ci + 1] ? starts[ci + 1] - st : sub]] : [];
    const sd = stepSec();
    if (st === 0) Tone.getDraw().schedule(() => opts.onBar?.(barIdx), time);
    Tone.getDraw().schedule(() => opts.onStep?.(barIdx, st), time);
    if (opts.metronome && st % sub === 0) r.click.triggerAttackRelease(st === 0 ? "C6" : "G5", "32n", time, 0.45);

    if (opts.parts.drums) {
      const pat = kind === "hit" ? hitBar(steps) : kind === "fill" ? { ...style.drums, ...style.fill } : style.drums;
      if (st === 0 && barIdx === (L ? L.start : 0) && kind === "groove") hit(r, "c", 0.75, time);
      for (const [voice, line] of Object.entries(pat)) {
        const ch = line[st];
        if (!ch || ch === ".") continue;
        const onBeat = st % sub === 0;
        let vel = ch === "a" ? 0.95 : ch === "g" ? 0.22 : 0.68;
        if (voice === "h" && ch === "x") vel = onBeat ? 0.6 : 0.42;
        if (voice === "r" && ch === "x") vel = onBeat ? 0.55 : 0.4;
        hit(r, ch === "o" && voice === "h" ? "o" : voice, vel, time);
      }
    }
    if (opts.parts.bass && chord) {
      const pat = kind === "hit" ? [[0, 0, sub]] : style.bass;
      const extra = freshHit.length && !pat.some(([s]) => s === st) ? [[st, 0, freshHit[0][1]]] : [];
      for (const [s, iv, len] of [...pat, ...extra]) {
        if (s !== st) continue;
        const next = barChords(track.bars[(barIdx + 1) % track.bars.length], keyRoot)[0];
        const pc = iv === "a" ? (next ? next.root - 1 : chord.root) : chord.root + (iv === 3 ? chord.intervals[1] : iv);
        r.bass.triggerAttackRelease(Tone.Frequency(fit(36 + pc, 28, 45), "midi"), len * sd, time + Math.max(0, human(0.008)), 0.75 + human(0.15));
      }
    }
    const comp = (inst, pattern, lo, hi, vel) => {
      const pat = freshHit.length && !pattern.some(([s]) => s === st) ? [...pattern, ...freshHit] : pattern;
      for (const [s, len] of pat) {
        if (s !== st || !chord) continue;
        const notes = chord.intervals.map((iv) => Tone.Frequency(fit(48 + chord.root + iv, lo, hi), "midi"));
        notes.forEach((nt, i) => inst.triggerAttackRelease(nt, len * sd, time + i * 0.006 + Math.max(0, human(0.01)), vel * (0.85 + Math.random() * 0.2)));
      }
    };
    if (opts.parts.keys) comp(r.piano, kind === "hit" ? stab : style.keys, 52, 70, 0.55);
    if (opts.parts.guitar) comp(r.guitar, kind === "hit" ? stab : style.guitar || style.keys, 52, 71, 0.6);
    if (opts.parts.lead) {
      const lead = r[opts.lead] || r.voice;
      if (melodyEvents.length) {
        for (const ev of melodyEvents[barIdx] || []) {
          if (ev.step !== st || ev.note === "r") continue;
          lead.triggerAttackRelease(Tone.Frequency(midiOf(ev.note) + opts.semitones, "midi"), ev.len * sd * 0.95, time, 0.75);
        }
      } else if (st === 0 && chord) {
        const top = [chord.intervals[1], chord.intervals[chord.intervals.length - 1]].map((iv) => Tone.Frequency(fit(60 + chord.root + iv, 62, 77), "midi"));
        lead.triggerAttackRelease(opts.lead === "sax" ? top.slice(0, 1) : top, (kind === "hit" ? sub : steps) * sd * 0.92, time, 0.4);
      }
    }
    if (tick % sub === 0) { const down = (tick / sub) % beats === 0; Tone.getDraw().schedule(() => beatPulse(down), time); }
    tick += 1;
  };
  T.scheduleRepeat((time) => { try { step(time); } catch (e) { console.warn("playalong", e.message); } }, STEP_DUR[sub]);
  T.start("+0.1");
}

export function setBandVolume(v) { if (rig) rig.out.gain.rampTo(v, 0.05); }
export function setClickVolume(v) { if (rig) rig.clickOut.gain.rampTo(v, 0.05); }

// Mixes the microphone (and optionally the band) into one recordable stream.
export async function createRecording(micStream, includeBand) {
  const r = await getRig();
  const raw = Tone.getContext().rawContext;
  const dest = raw.createMediaStreamDestination();
  const mic = raw.createMediaStreamSource(micStream);
  mic.connect(dest);
  if (includeBand) Tone.connect(r.out, dest);
  return { stream: dest.stream, cleanup: () => { mic.disconnect(); if (includeBand) r.out.disconnect(dest); } };
}

export function setTempo(bpm) { Tone.getTransport().bpm.rampTo(bpm, 0.2); }

export function stopTrack() {
  const T = Tone.getTransport();
  if (T.state === "started") soundNotice("Backing track stopped");
  T.stop();
  T.cancel(0);
  if (rig) ["piano", "bass", "guitar", "sax", "voice"].forEach((k) => rig[k].releaseAll?.());
}
